import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, Loader2, X, Check, AlertCircle, Plus, Package, Truck } from 'lucide-react';
import portalAPI from '../../services/portalAPI';

interface OrderItem {
  feed_id: number;
  name: string;
  form: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  animal_type?: string;
}

interface Suggestion {
  feed_id: number;
  name: string;
  suggested_qty: number;
  reason: string;
}

interface ClarificationItem {
  name: string;
  feed_id: number;
  quantity: number;
  available_forms: string[];
  price_mash: number;
  price_pellet: number;
}

interface VoiceOrderResult {
  response: string;
  draft_order: OrderItem[];
  bag_count: number;
  total_amount: number;
  shipping_status: 'free' | 'paid' | 'preferred' | 'pending';
  shipping_cost: number;
  shipping_message: string;
  bags_to_free: number;
  bags_to_preferred: number;
  suggestions: Suggestion[];
  estimated_ship_date: string;
  transcription?: string;
  error?: string;
  needs_clarification?: boolean;
  clarification_item?: ClarificationItem;
  pending_items?: any[];
}

type VoiceOrderState = 'idle' | 'recording' | 'transcribing' | 'processing' | 'preview' | 'clarification' | 'confirming' | 'success' | 'error';

interface VoiceOrderAssistantProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

const FREE_SHIPPING_BAGS = 50;
const PREFERRED_BAGS = 65;

const VoiceOrderAssistant: React.FC<VoiceOrderAssistantProps> = ({ onSuccess, onClose }) => {
  const [state, setState] = useState<VoiceOrderState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [result, setResult] = useState<VoiceOrderResult | null>(null);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [pendingItems, setPendingItems] = useState<any[] | null>(null);
  const [clarificationItem, setClarificationItem] = useState<ClarificationItem | null>(null);
  const [selectionMode, setSelectionMode] = useState<'mash' | 'pellet' | 'split'>('mash');
  const [splitMashQty, setSplitMashQty] = useState(0);
  const [splitPelletQty, setSplitPelletQty] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    audioChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
      setState('recording');
      setRecordingDuration(0);

      timerRef.current = setInterval(() => {
        setRecordingDuration((d) => d + 1);
      }, 1000);

    } catch (err: unknown) {
      console.error('Failed to start recording:', err);
      if (err instanceof Error && err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access.');
      } else {
        setError('Failed to start recording. Please check your microphone.');
      }
      setState('error');
    }
  }, []);

  const handleStopAndProcess = useCallback(async () => {
    stopRecording();
    setState('transcribing');

    // Wait for audio chunks
    await new Promise(resolve => setTimeout(resolve, 100));

    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

    if (audioBlob.size === 0) {
      setError('No audio recorded. Please try again.');
      setState('error');
      return;
    }

    setState('processing');

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await portalAPI.post('/portal/voice-order/process/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      // Check if clarification is needed
      if (response.data.needs_clarification && response.data.clarification_item) {
        const item = response.data.clarification_item;
        setResult(response.data);
        setClarificationItem(item);
        setPendingItems(response.data.pending_items);
        // Initialize split quantities to half each
        const halfQty = Math.floor(item.quantity / 2);
        setSplitMashQty(halfQty);
        setSplitPelletQty(item.quantity - halfQty);
        setSelectionMode('mash'); // Default to mash
        setState('clarification');
      } else {
        setResult(response.data);
        setState('preview');
      }

    } catch (err: any) {
      console.error('Processing error:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to process order');
      setState('error');
    }
  }, [stopRecording]);

  const handleCancel = useCallback(() => {
    stopRecording();
    setState('idle');
    setRecordingDuration(0);
    setError(null);
    setResult(null);
  }, [stopRecording]);

  const handleAddSuggestion = async (suggestion: Suggestion) => {
    if (!result) return;

    try {
      const response = await portalAPI.post('/portal/voice-order/add_item/', {
        draft_order: result.draft_order,
        feed_id: suggestion.feed_id,
        quantity: suggestion.suggested_qty,
      });

      setResult({
        ...result,
        ...response.data,
        suggestions: result.suggestions.filter(s => s.feed_id !== suggestion.feed_id),
      });
    } catch (err) {
      console.error('Add item error:', err);
    }
  };

  const handleConfirmSelection = async () => {
    if (!clarificationItem || !pendingItems) return;

    setState('processing');

    try {
      let requestData: any = {
        pending_items: pendingItems,
        clarification_item: clarificationItem,
      };

      if (selectionMode === 'split') {
        // Split selection - send both forms with quantities
        requestData.selections = [
          { form: 'Mash', quantity: splitMashQty },
          { form: 'Pellet', quantity: splitPelletQty },
        ].filter(s => s.quantity > 0);
      } else {
        // Single form selection
        requestData.selected_form = selectionMode === 'mash' ? 'Mash' : 'Pellet';
      }

      const response = await portalAPI.post('/portal/voice-order/clarify/', requestData);

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      // Check if more clarifications are needed
      if (response.data.needs_clarification && response.data.clarification_item) {
        const item = response.data.clarification_item;
        setResult(response.data);
        setClarificationItem(item);
        setPendingItems(response.data.pending_items);
        const halfQty = Math.floor(item.quantity / 2);
        setSplitMashQty(halfQty);
        setSplitPelletQty(item.quantity - halfQty);
        setSelectionMode('mash');
        setState('clarification');
      } else {
        setResult(response.data);
        setClarificationItem(null);
        setPendingItems(null);
        setState('preview');
      }
    } catch (err: any) {
      console.error('Clarification error:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to process selection');
      setState('error');
    }
  };

  const handleSplitQtyChange = (form: 'mash' | 'pellet', value: number) => {
    if (!clarificationItem) return;
    const maxQty = clarificationItem.quantity;

    if (form === 'mash') {
      const newMash = Math.max(0, Math.min(value, maxQty));
      setSplitMashQty(newMash);
      setSplitPelletQty(maxQty - newMash);
    } else {
      const newPellet = Math.max(0, Math.min(value, maxQty));
      setSplitPelletQty(newPellet);
      setSplitMashQty(maxQty - newPellet);
    }
  };

  const handleConfirmOrder = async () => {
    if (!result || result.draft_order.length === 0) return;

    setState('confirming');

    try {
      const response = await portalAPI.post('/portal/voice-order/confirm/', {
        draft_order: result.draft_order,
      });

      if (response.data.success) {
        setOrderNumber(response.data.order_number);
        setState('success');
        setTimeout(() => {
          onSuccess?.();
        }, 3000);
      } else {
        throw new Error(response.data.error || 'Failed to place order');
      }
    } catch (err: any) {
      console.error('Confirm error:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to place order');
      setState('error');
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getShippingBadge = () => {
    if (!result) return null;

    if (result.shipping_status === 'preferred') {
      return (
        <div style={styles.shippingBadge.preferred}>
          <Truck size={16} />
          <span>FREE SHIPPING - Full Pallet!</span>
        </div>
      );
    } else if (result.shipping_status === 'free') {
      return (
        <div style={styles.shippingBadge.free}>
          <Truck size={16} />
          <span>FREE SHIPPING</span>
        </div>
      );
    } else {
      return (
        <div style={styles.shippingBadge.paid}>
          <AlertCircle size={16} />
          <span>{result.bags_to_free} bags away from FREE SHIPPING</span>
        </div>
      );
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <span style={styles.lauraIcon}>🎤</span>
            <div>
              <h2 style={styles.title}>Quick Voice Order</h2>
              <p style={styles.subtitle}>Order feed with your voice</p>
            </div>
          </div>
          <button onClick={onClose || handleCancel} style={styles.closeBtn}>
            <X size={24} />
          </button>
        </div>

        <div style={styles.body}>
          {/* Idle State - Big Mic Button */}
          {state === 'idle' && (
            <div style={styles.idleContainer}>
              <button onClick={startRecording} style={styles.micButton}>
                <Mic size={48} color="white" />
              </button>
              <p style={styles.micHint}>Tap to speak your order</p>
              <div style={styles.examples}>
                <p style={styles.exampleTitle}>Try saying:</p>
                <p style={styles.example}>"30 bags of layer pellets"</p>
                <p style={styles.example}>"Reorder what I got last time"</p>
                <p style={styles.example}>"What should I order?"</p>
              </div>
              <div style={styles.shippingInfo}>
                <Package size={16} />
                <span>50+ bags = FREE SHIPPING</span>
              </div>
            </div>
          )}

          {/* Recording State */}
          {state === 'recording' && (
            <div style={styles.recordingContainer}>
              <div style={styles.recordingIndicator}>
                <div style={styles.recordingDot} />
                <span style={styles.recordingTime}>{formatDuration(recordingDuration)}</span>
              </div>
              <p style={styles.recordingHint}>Listening... Tell me what you need</p>
              <div style={styles.recordingActions}>
                <button onClick={handleCancel} style={styles.cancelBtn}>
                  <X size={20} /> Cancel
                </button>
                <button onClick={handleStopAndProcess} style={styles.doneBtn}>
                  <Check size={20} /> Done
                </button>
              </div>
            </div>
          )}

          {/* Processing States */}
          {(state === 'transcribing' || state === 'processing') && (
            <div style={styles.processingContainer}>
              <Loader2 size={48} style={styles.spinner} />
              <p style={styles.processingText}>
                {state === 'transcribing' ? 'Listening...' : 'Laura is processing your order...'}
              </p>
            </div>
          )}

          {/* Clarification State - Ask for form preference */}
          {state === 'clarification' && clarificationItem && (
            <div style={styles.clarificationContainer}>
              {/* Show what was transcribed so user can verify */}
              {result?.transcription && (
                <div style={styles.transcriptionBox}>
                  <div>
                    <span style={styles.transcriptionLabel}>I heard:</span>
                    <em style={styles.transcriptionText}>"{result.transcription}"</em>
                  </div>
                  <button onClick={handleCancel} style={styles.retryLink}>
                    Not right? Try again
                  </button>
                </div>
              )}
              <div style={styles.lauraResponse}>
                <span style={styles.lauraAvatar}>🤖</span>
                <div style={styles.lauraText}>
                  <p style={{ margin: '4px 0', fontWeight: 600 }}>
                    For {clarificationItem.quantity} bags of {clarificationItem.name} -
                  </p>
                  <p style={{ margin: '4px 0' }}>How would you like them?</p>
                </div>
              </div>

              <div style={styles.formOptions}>
                {/* All Mash Option */}
                <label style={styles.formOption}>
                  <input
                    type="radio"
                    name="formSelection"
                    checked={selectionMode === 'mash'}
                    onChange={() => setSelectionMode('mash')}
                    style={styles.radioInput}
                  />
                  <div style={styles.formOptionContent}>
                    <span style={styles.formOptionLabel}>All Mash</span>
                    <span style={styles.formOptionPrice}>
                      {clarificationItem.quantity} bags @ ${clarificationItem.price_mash?.toFixed(2)}/bag =
                      <strong> ${(clarificationItem.quantity * (clarificationItem.price_mash || 0)).toFixed(2)}</strong>
                    </span>
                  </div>
                </label>

                {/* All Pellet Option */}
                <label style={styles.formOption}>
                  <input
                    type="radio"
                    name="formSelection"
                    checked={selectionMode === 'pellet'}
                    onChange={() => setSelectionMode('pellet')}
                    style={styles.radioInput}
                  />
                  <div style={styles.formOptionContent}>
                    <span style={styles.formOptionLabel}>All Pellet</span>
                    <span style={styles.formOptionPrice}>
                      {clarificationItem.quantity} bags @ ${clarificationItem.price_pellet?.toFixed(2)}/bag =
                      <strong> ${(clarificationItem.quantity * (clarificationItem.price_pellet || 0)).toFixed(2)}</strong>
                    </span>
                  </div>
                </label>

                {/* Split Option */}
                <label style={styles.formOption}>
                  <input
                    type="radio"
                    name="formSelection"
                    checked={selectionMode === 'split'}
                    onChange={() => setSelectionMode('split')}
                    style={styles.radioInput}
                  />
                  <div style={styles.formOptionContent}>
                    <span style={styles.formOptionLabel}>Split Order</span>
                    {selectionMode === 'split' && (
                      <div style={styles.splitInputs}>
                        <div style={styles.splitRow}>
                          <label style={styles.splitLabel}>Mash:</label>
                          <input
                            type="number"
                            value={splitMashQty}
                            onChange={(e) => handleSplitQtyChange('mash', parseInt(e.target.value) || 0)}
                            min={0}
                            max={clarificationItem.quantity}
                            style={styles.splitInput}
                          />
                          <span style={styles.splitBags}>bags</span>
                        </div>
                        <div style={styles.splitRow}>
                          <label style={styles.splitLabel}>Pellet:</label>
                          <input
                            type="number"
                            value={splitPelletQty}
                            onChange={(e) => handleSplitQtyChange('pellet', parseInt(e.target.value) || 0)}
                            min={0}
                            max={clarificationItem.quantity}
                            style={styles.splitInput}
                          />
                          <span style={styles.splitBags}>bags</span>
                        </div>
                        <div style={styles.splitTotal}>
                          Total: ${(
                            splitMashQty * (clarificationItem.price_mash || 0) +
                            splitPelletQty * (clarificationItem.price_pellet || 0)
                          ).toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>

              <div style={styles.clarificationActions}>
                <button onClick={handleCancel} style={styles.clarificationCancel}>
                  Cancel
                </button>
                <button onClick={handleConfirmSelection} style={styles.clarificationConfirm}>
                  Confirm Selection
                </button>
              </div>
            </div>
          )}

          {/* Preview State - Order Summary */}
          {state === 'preview' && result && (
            <div style={styles.previewContainer}>
              {/* Show what was transcribed so user can verify */}
              {result.transcription && (
                <div style={styles.transcriptionBox}>
                  <div>
                    <span style={styles.transcriptionLabel}>I heard:</span>
                    <em style={styles.transcriptionText}>"{result.transcription}"</em>
                  </div>
                  <button onClick={handleCancel} style={styles.retryLink}>
                    Not right? Try again
                  </button>
                </div>
              )}
              {/* Laura's Response */}
              <div style={styles.lauraResponse}>
                <span style={styles.lauraAvatar}>🤖</span>
                <div style={styles.lauraText}>
                  {result.response.split('\n').map((line, i) => (
                    <p key={i} style={{ margin: '4px 0' }}>{line}</p>
                  ))}
                </div>
              </div>

              {/* Order Summary Card */}
              {result.draft_order.length > 0 && (
                <div style={styles.orderCard}>
                  <div style={styles.orderCardHeader}>
                    <Package size={20} />
                    <span>Your Order</span>
                  </div>

                  <div style={styles.orderItems}>
                    {result.draft_order.map((item, index) => (
                      <div key={index} style={styles.orderItem}>
                        <span style={styles.itemQty}>{item.quantity}x</span>
                        <span style={styles.itemName}>
                          {item.name} {item.form && <span style={styles.itemForm}>{item.form}</span>}
                        </span>
                        <span style={styles.itemPrice}>{formatCurrency(item.line_total)}</span>
                      </div>
                    ))}
                  </div>

                  <div style={styles.orderTotals}>
                    <div style={styles.totalRow}>
                      <span>Total: {result.bag_count} bags</span>
                      <span style={styles.totalAmount}>{formatCurrency(result.total_amount)}</span>
                    </div>
                    {getShippingBadge()}
                    {result.shipping_status === 'paid' && result.shipping_cost > 0 && (
                      <div style={styles.shippingCost}>
                        + Shipping: {formatCurrency(result.shipping_cost)}
                      </div>
                    )}
                    {result.estimated_ship_date && (
                      <div style={styles.shipDate}>
                        <Truck size={14} />
                        <span>Ships: {result.estimated_ship_date}</span>
                      </div>
                    )}
                  </div>

                  {/* Suggestions for free shipping */}
                  {result.suggestions && result.suggestions.length > 0 && result.shipping_status === 'paid' && (
                    <div style={styles.suggestions}>
                      <p style={styles.suggestionsTitle}>Add to get FREE SHIPPING:</p>
                      {result.suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleAddSuggestion(suggestion)}
                          style={styles.suggestionBtn}
                        >
                          <Plus size={16} />
                          <span>{suggestion.suggested_qty} {suggestion.name}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div style={styles.orderActions}>
                    <button onClick={handleCancel} style={styles.modifyBtn}>
                      Modify
                    </button>
                    <button onClick={handleConfirmOrder} style={styles.confirmBtn}>
                      <Check size={18} />
                      Confirm Order
                    </button>
                  </div>
                </div>
              )}

              {/* No items matched */}
              {result.draft_order.length === 0 && (
                <div style={styles.noItems}>
                  <p>Try again with a specific product and quantity.</p>
                  <button onClick={handleCancel} style={styles.tryAgainBtn}>
                    Try Again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Confirming State */}
          {state === 'confirming' && (
            <div style={styles.processingContainer}>
              <Loader2 size={48} style={styles.spinner} />
              <p style={styles.processingText}>Placing your order...</p>
            </div>
          )}

          {/* Success State */}
          {state === 'success' && (
            <div style={styles.successContainer}>
              <div style={styles.successIcon}>
                <Check size={48} color="white" />
              </div>
              <h3 style={styles.successTitle}>Order Placed!</h3>
              <p style={styles.successText}>
                Order #{orderNumber} has been submitted.
              </p>
              {result && (
                <p style={styles.successShip}>
                  Ships: {result.estimated_ship_date}
                </p>
              )}
            </div>
          )}

          {/* Error State */}
          {state === 'error' && (
            <div style={styles.errorContainer}>
              <AlertCircle size={48} color="#dc2626" />
              <p style={styles.errorText}>{error}</p>
              <button onClick={handleCancel} style={styles.tryAgainBtn}>
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes recordPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties | any } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    maxWidth: '480px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#8B0000',
    color: 'white',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  lauraIcon: {
    fontSize: '28px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
  },
  subtitle: {
    margin: 0,
    fontSize: '13px',
    opacity: 0.9,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    padding: '4px',
    opacity: 0.8,
  },
  body: {
    padding: '24px',
    maxHeight: 'calc(90vh - 80px)',
    overflowY: 'auto',
  },
  idleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '20px 0',
  },
  micButton: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 30px rgba(220, 38, 38, 0.4)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  micHint: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#374151',
    margin: 0,
  },
  examples: {
    textAlign: 'center',
    marginTop: '16px',
  },
  exampleTitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '0 0 8px 0',
  },
  example: {
    fontSize: '14px',
    color: '#9ca3af',
    fontStyle: 'italic',
    margin: '4px 0',
  },
  shippingInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '24px',
    padding: '10px 16px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    color: '#166534',
    fontSize: '14px',
    fontWeight: 500,
  },
  recordingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    padding: '40px 20px',
    backgroundColor: '#fef2f2',
    borderRadius: '12px',
    margin: '-24px',
    marginBottom: '0',
  },
  recordingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  recordingDot: {
    width: '16px',
    height: '16px',
    backgroundColor: '#dc2626',
    borderRadius: '50%',
    animation: 'recordPulse 1s infinite',
  },
  recordingTime: {
    fontSize: '36px',
    fontWeight: 700,
    fontFamily: 'monospace',
    color: '#dc2626',
  },
  recordingHint: {
    fontSize: '16px',
    color: '#991b1b',
    margin: 0,
  },
  recordingActions: {
    display: 'flex',
    gap: '16px',
  },
  cancelBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 24px',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    color: '#6b7280',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  doneBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 24px',
    backgroundColor: '#10b981',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  processingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '60px 20px',
  },
  spinner: {
    animation: 'spin 1s linear infinite',
    color: '#8B0000',
  },
  processingText: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  previewContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  transcriptionBox: {
    backgroundColor: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '12px',
  },
  transcriptionLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#92400e',
    marginRight: '8px',
  },
  transcriptionText: {
    fontSize: '14px',
    color: '#78350f',
  },
  retryLink: {
    background: 'none',
    border: 'none',
    color: '#92400e',
    fontSize: '12px',
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: '4px 0',
    marginTop: '4px',
  },
  lauraResponse: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
  },
  lauraAvatar: {
    fontSize: '24px',
    flexShrink: 0,
  },
  lauraText: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.5,
  },
  orderCard: {
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  orderCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    fontWeight: 600,
    color: '#374151',
  },
  orderItems: {
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb',
  },
  orderItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  itemQty: {
    fontWeight: 600,
    color: '#374151',
    marginRight: '8px',
    minWidth: '40px',
  },
  itemName: {
    flex: 1,
    color: '#374151',
  },
  itemForm: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    padding: '2px 6px',
    borderRadius: '4px',
    marginLeft: '6px',
    textTransform: 'uppercase',
  },
  itemPrice: {
    fontWeight: 500,
    color: '#6b7280',
  },
  orderTotals: {
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  totalAmount: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827',
  },
  shippingBadge: {
    free: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 12px',
      backgroundColor: '#dcfce7',
      borderRadius: '6px',
      color: '#166534',
      fontWeight: 600,
      fontSize: '14px',
      marginBottom: '8px',
    },
    preferred: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 12px',
      backgroundColor: '#dbeafe',
      borderRadius: '6px',
      color: '#1e40af',
      fontWeight: 600,
      fontSize: '14px',
      marginBottom: '8px',
    },
    paid: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 12px',
      backgroundColor: '#fef3c7',
      borderRadius: '6px',
      color: '#92400e',
      fontWeight: 600,
      fontSize: '14px',
      marginBottom: '8px',
    },
  },
  shippingCost: {
    fontSize: '14px',
    color: '#dc2626',
    fontWeight: 500,
    marginBottom: '8px',
  },
  shipDate: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#6b7280',
  },
  suggestions: {
    padding: '12px 16px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#fffbeb',
  },
  suggestionsTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#92400e',
    margin: '0 0 8px 0',
  },
  suggestionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    padding: '10px 12px',
    marginBottom: '6px',
    backgroundColor: 'white',
    border: '1px solid #fcd34d',
    borderRadius: '6px',
    color: '#92400e',
    fontSize: '14px',
    cursor: 'pointer',
    textAlign: 'left',
  },
  orderActions: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    borderTop: '1px solid #e5e7eb',
  },
  modifyBtn: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    color: '#374151',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  confirmBtn: {
    flex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#10b981',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  noItems: {
    textAlign: 'center',
    padding: '20px',
    color: '#6b7280',
  },
  tryAgainBtn: {
    marginTop: '12px',
    padding: '10px 20px',
    backgroundColor: '#8B0000',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '40px 20px',
    textAlign: 'center',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#111827',
    margin: 0,
  },
  successText: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  successShip: {
    fontSize: '14px',
    color: '#10b981',
    fontWeight: 500,
    margin: 0,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '40px 20px',
    textAlign: 'center',
  },
  errorText: {
    fontSize: '15px',
    color: '#dc2626',
    margin: 0,
  },
  clarificationContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px 0',
  },
  formOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  formOption: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: '#f9fafb',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
  },
  radioInput: {
    width: '20px',
    height: '20px',
    marginTop: '2px',
    accentColor: '#8B0000',
  },
  formOptionContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  formOptionLabel: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
  },
  formOptionPrice: {
    fontSize: '14px',
    color: '#6b7280',
  },
  splitInputs: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  splitRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  splitLabel: {
    width: '60px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  splitInput: {
    width: '70px',
    padding: '8px 10px',
    fontSize: '16px',
    fontWeight: 600,
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    textAlign: 'center',
  },
  splitBags: {
    fontSize: '14px',
    color: '#6b7280',
  },
  splitTotal: {
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #e5e7eb',
    fontSize: '15px',
    fontWeight: 600,
    color: '#111827',
    textAlign: 'right',
  },
  clarificationActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  clarificationCancel: {
    flex: 1,
    padding: '12px 16px',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    color: '#6b7280',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  clarificationConfirm: {
    flex: 2,
    padding: '12px 16px',
    backgroundColor: '#8B0000',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default VoiceOrderAssistant;
